// Transcript panel pieces (UX spec 6.6, FE-16 subset): the status line and the segment log.
// Partial text is provisional and labeled so; a transcription outage never touches call controls.
import type { Call, Capability, Transcript } from '@bcio-voice/contracts';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { callAnswered, transcriptStateText } from '../../call/mediaStatus.ts';
import { hasPermission, usable } from '../../lib/capabilities.ts';
import { formatDuration } from '../../lib/format.ts';
import { useWorkspace } from '../../state/workspace.tsx';
import { ErrorPanel, SkeletonRows, StatePanel } from '../../ui/StatePanel.tsx';
import { LiveTranscriptPoller, type LiveTranscriptState } from './liveTranscript.ts';

/** What the status line and panel read from a call: its state and whether anyone answered it. */
export type TranscriptCall = Pick<Call, 'transcription_state'> & Partial<Pick<Call, 'state' | 'answered_at' | 'outcome'>>;

/**
 * Canonical transcription state; no automatic recovery or latency is inferred. `segments` is the loaded
 * transcript's segment count (undefined until it is loaded), which decides what a 'delayed' call says.
 */
export function transcriptStatusLine(call: TranscriptCall, cap: Capability | null, segments?: number): { text: string; tone: 'neutral' | 'live' | 'warn' } {
  return transcriptStateText(call.transcription_state, usable(cap), { answered: callAnswered(call), segments, finished: callFinished(call) });
}

/** The call has ended or failed (its transcript can still be arriving for a moment). */
export const callFinished = (call: Partial<Pick<Call, 'state'>>): boolean => call.state === 'ended' || call.state === 'failed';

export type SpeakerName = (participantId: string | null) => string;

type Gap = Transcript['gaps'][number];

/** A quiet inline row for a stretch without transcript text. The reason code is a diagnostic, kept off the page. */
export function gapText(gap: Gap): string {
  const span = `${formatDuration(gap.start_ms)}–${formatDuration(gap.end_ms)}`;
  return gap.reason === 'privacy_pause' ? `Transcription paused ${span}` : `Not transcribed ${span}`;
}

type LogRow =
  | { kind: 'segment'; at: number; segment: Transcript['segments'][number] }
  | { kind: 'gap'; at: number; gap: Gap; index: number };

/** Segments and gaps in time order (stable, so a segment stays ahead of a gap that starts at the same moment). */
function logRows(transcript: Pick<Transcript, 'segments' | 'gaps'>): LogRow[] {
  const rows: LogRow[] = [
    ...transcript.segments.filter(segment => segment.text.trim().length > 0).map(segment => ({ kind: 'segment' as const, at: segment.start_ms, segment })),
    ...transcript.gaps.map((gap, index) => ({ kind: 'gap' as const, at: gap.start_ms, gap, index })),
  ];
  return rows.sort((a, b) => a.at - b.at);
}

/** The transcript as a log: `role="log"`, not announced by default (6.6 screen-reader rule). */
export function TranscriptLog({ transcript, speaker }: { transcript: Pick<Transcript, 'segments' | 'gaps'>; speaker: SpeakerName }) {
  return (
    <ol className="transcript" role="log" aria-live="off" aria-label="Transcript">
      {logRows(transcript).map(row => {
        if (row.kind === 'gap') {
          const g = row.gap;
          return (
            <li key={`gap-${row.index}-${g.start_ms}-${g.end_ms}`} className="seg seg-gap" data-reason={g.reason}>
              {gapText(g)}
            </li>
          );
        }
        const s = row.segment;
        return (
          <li key={s.id} className={s.is_final ? 'seg' : 'seg seg-partial'} data-revision={s.revision}>
            <span className="seg-who">{speaker(s.participant_id)}</span>
            <span className="seg-time num">{formatDuration(s.start_ms)}</span>
            <span className="seg-text">{s.text}{!s.is_final ? <span className="visually-hidden"> (in progress)</span> : null}</span>
            {s.source === 'refined' ? <span className="seg-mark">Refined</span> : !s.is_final ? <span className="seg-mark" aria-hidden="true">Provisional</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

export interface CallTranscriptViewProps {
  call: Pick<Call, 'id' | 'state' | 'transcription_state'> & Partial<Pick<Call, 'answered_at' | 'outcome'>>;
  status: { text: string; tone: 'neutral' | 'live' | 'warn' };
  state: LiveTranscriptState;
  streamReconnecting?: boolean;
  speaker: SpeakerName;
  onRetry?: () => void;
}

/**
 * The segment log for one call. A 'delayed' call (where nearly every finished call ends) shows the segments it has
 * with quiet gap rows. The "Transcription is unavailable" panel is only for a failed transcript, or for a call
 * that was answered and still produced no text.
 */
export function CallTranscriptView({
  call,
  status,
  state,
  streamReconnecting = false,
  speaker,
  onRetry = () => undefined,
}: CallTranscriptViewProps) {
  const t = state.transcript;
  const activeOrHeld = call.state === 'active' || call.state === 'held';
  const isInitialLoading = !t && !state.errorView && (state.loading || !state.initialized);
  const segmentCount = t?.segments.filter(segment => segment.text.trim().length > 0).length ?? 0;
  const failed = call.transcription_state === 'failed' || t?.state === 'failed';
  const delayed = !failed && (call.transcription_state === 'delayed' || t?.state === 'delayed');
  const answered = callAnswered(call) === true;
  const failedDetail = call.transcription_state === 'failed' ? status.text : transcriptStateText('failed', true).text;

  if (isInitialLoading) {
    return <SkeletonRows rows={2} label="Loading transcript" />;
  }
  if (state.errorView && !t) {
    return <ErrorPanel error={state.errorView} onRetry={onRetry} compact />;
  }
  return (
    <>
      {streamReconnecting ? (
        <p className="meta tone-warn" role="status">
          Event stream reconnecting. Transcript will catch up when reconnected.
        </p>
      ) : null}
      {state.error && t ? (
        <p className="meta tone-warn" role="status">
          {state.error}
        </p>
      ) : null}
      {!t || segmentCount === 0 ? (
        <>
          {failed ? (
            <StatePanel variant="dependency" compact headingLevel={3} title="Transcription is unavailable" body={failedDetail} />
          ) : delayed && answered ? (
            <StatePanel variant="dependency" compact headingLevel={3} title="Transcription is unavailable" body="No transcript captured. Recording status is shown separately." />
          ) : delayed ? (
            <StatePanel variant="empty" compact headingLevel={3} title="No transcript captured" />
          ) : callFinished(call) && (call.transcription_state === 'live' || call.transcription_state === 'pending') ? (
            <StatePanel variant="empty" compact headingLevel={3} title="Transcript processing" body="The call has ended. Its lines appear here as they are written." />
          ) : (
            <StatePanel
              variant="empty"
              compact
              headingLevel={3}
              title="No transcript text yet"
              body={
                t?.gaps.length
                  ? `${t.gaps.length} gap${t.gaps.length === 1 ? '' : 's'} recorded.`
                  : activeOrHeld
                    ? 'Listening for speech. Provisional lines appear here as people speak and update when finalized.'
                    : undefined
              }
            />
          )}
          {t && t.gaps.length > 0 ? <TranscriptLog transcript={t} speaker={speaker} /> : null}
        </>
      ) : (
        <>
          {failed && call.transcription_state !== 'failed' ? (
            <p className="meta tone-warn" role="status">
              {failedDetail}
            </p>
          ) : null}
          <TranscriptLog transcript={t} speaker={speaker} />
        </>
      )}
    </>
  );
}

export interface CallTranscript {
  state: LiveTranscriptState;
  reload: () => void;
}

/**
 * The call's transcript for the call-detail page, shared by the status line (which needs the segment count) and
 * the segment log. Applies partial/final/corrected/gap SSE events in-place, polls while active/held, preserves
 * already-authorized retained segments across same-call state transitions or workspace degradation, and
 * immediately clears on call change, unmount, or media-access revocation.
 */
export function useCallTranscript(call: Call): CallTranscript {
  const ws = useWorkspace();
  const allowed = hasPermission(ws.permissions, 'transcripts:read');
  const enabled = allowed && call.transcription_state !== 'off' && ws.mediaAccess.state === 'ready';
  const poller = useMemo(
    () => new LiveTranscriptPoller(ws.api, ws.mediaAccessStore, { includeEnded: true }),
    [ws.api, ws.mediaAccessStore, call.id, ws.mediaAccess.epoch],
  );
  const state = useSyncExternalStore(poller.subscribe, poller.snapshot, poller.snapshot);

  useEffect(() => () => poller.stop(), [poller]);
  useEffect(() => {
    poller.start(call, enabled);
  }, [poller, call.id, call.state, call.transcription_state, enabled, ws.mediaAccess.epoch]);

  return { state, reload: poller.reload };
}

/** Call-detail transcript viewer (active/held and historical calls), fed by useCallTranscript. */
export function CallTranscriptBody({
  call,
  status,
  transcript,
}: {
  call: Call;
  status: { text: string; tone: 'neutral' | 'live' | 'warn' };
  transcript: CallTranscript;
}) {
  const ws = useWorkspace();
  const activeOrHeld = call.state === 'active' || call.state === 'held';
  const streamReconnecting = activeOrHeld && ws.stream.state === 'reconnecting';
  const speaker: SpeakerName = pid => {
    const part = call.participants.find(p => p.id === pid);
    if (part?.user_id) return ws.userName(part.user_id) ?? 'Teammate';
    if (part?.kind === 'external') return 'Customer / external caller';
    return 'Unidentified speaker';
  };

  return (
    <CallTranscriptView
      call={call}
      status={status}
      state={transcript.state}
      streamReconnecting={streamReconnecting}
      speaker={speaker}
      onRetry={transcript.reload}
    />
  );
}
